import {
  forwardRef,
  type ElementRef,
  type ComponentPropsWithoutRef,
} from 'react';
import { Root, List, Trigger, Content } from '@radix-ui/react-tabs';

import { cn } from '@/lib/utils';
import { cva, VariantProps } from 'class-variance-authority';

const Tabs = Root;

const TabsList = forwardRef<
  ElementRef<typeof List>,
  ComponentPropsWithoutRef<typeof List>
>(({ className, ...props }, ref) => (
  <List
    ref={ref}
    className={cn(
      'inline-flex items-center justify-center font-mono text-vestgrey-500',
      className,
    )}
    {...props}
  />
));
TabsList.displayName = List.displayName;

const triggerVariants = cva(
  'inline-flex items-center justify-center overflow-hidden whitespace-nowrap px-12 py-4 font-normal text-vestgrey-100 ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default:
          'bg-background uppercase data-[state=active]:border-b data-[state=active]:border-b-primary data-[state=active]:text-primary hover:bg-vestgrey-800 shadow-primary font-mono border-y border-border text-sm',
        underline:
          'border-b border-transparent font-medium text-base text-foreground-secondary data-[state=active]:border-b-foreground data-[state=active]:text-foreground',
        large:
          "leading-5 capitalize px-4 pt-0 pb-3 relative border-b border-transparent font-semibold text-3xl text-foreground-secondary data-[state=active]:text-foreground data-[state=active]:after:content-[''] data-[state=active]:after:absolute data-[state=active]:after:left-1/2 data-[state=active]:after:-translate-x-1/2 data-[state=active]:after:bottom-0 data-[state=active]:after:h-px data-[state=active]:after:w-7 data-[state=active]:after:bg-foreground",
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

const TabsTrigger = forwardRef<
  ElementRef<typeof Trigger>,
  ComponentPropsWithoutRef<typeof Trigger> &
  VariantProps<typeof triggerVariants>
>(({ className, children, variant, ...props }, ref) => (
  <Trigger
    ref={ref}
    className={cn(triggerVariants({ variant }), className)}
    {...props}
  >
    {children}
  </Trigger>
));
TabsTrigger.displayName = Trigger.displayName;

const TabsContent = forwardRef<
  ElementRef<typeof Content>,
  ComponentPropsWithoutRef<typeof Content>
>(({ className, ...props }, ref) => (
  <Content
    ref={ref}
    className={cn('focus-visible:outline-none', className)}
    {...props}
  />
));
TabsContent.displayName = Content.displayName;

export { Tabs, TabsList, TabsTrigger, TabsContent };
